import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { 
  BarChart3, 
  Send, 
  FileSpreadsheet,
  Users,
  Building2,
  Truck,
  TrendingUp,
  Filter
} from "lucide-react";

const API = '/api';

const MESES = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

export default function Relatorios() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const currentDate = new Date();
  
  const [ano, setAno] = useState(2025);
  const [mes, setMes] = useState(currentDate.getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [report, setReport] = useState(null);
  
  // Filtros
  const [farmacias, setFarmacias] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedFarmacias, setSelectedFarmacias] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [allFarmaciasSelected, setAllFarmaciasSelected] = useState(true);
  const [allUsersSelected, setAllUsersSelected] = useState(true);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [farmaciasRes, usersRes] = await Promise.all([
          axios.get(`${API}/farmacias`),
          isAdmin ? axios.get(`${API}/users`) : Promise.resolve({ data: [] })
        ]);
        setFarmacias(farmaciasRes.data);
        setUsers(usersRes.data);
        setSelectedFarmacias(farmaciasRes.data.map(f => f.id));
        setSelectedUsers(usersRes.data.map(u => u.id));
      } catch (error) {
        console.error("Erro ao carregar filtros:", error);
      }
    };
    fetchFilters();
  }, [isAdmin]);

  const toggleAllFarmacias = (checked) => {
    setAllFarmaciasSelected(checked);
    setSelectedFarmacias(checked ? farmacias.map(f => f.id) : []);
  };

  const toggleFarmacia = (farmaciaId) => {
    if (selectedFarmacias.includes(farmaciaId)) {
      setSelectedFarmacias(selectedFarmacias.filter(id => id !== farmaciaId));
      setAllFarmaciasSelected(false);
    } else {
      const newSelected = [...selectedFarmacias, farmaciaId];
      setSelectedFarmacias(newSelected);
      setAllFarmaciasSelected(newSelected.length === farmacias.length);
    }
  };

  const toggleAllUsers = (checked) => {
    setAllUsersSelected(checked);
    setSelectedUsers(checked ? users.map(u => u.id) : []);
  };

  const toggleUser = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
      setAllUsersSelected(false);
    } else {
      const newSelected = [...selectedUsers, userId];
      setSelectedUsers(newSelected);
      setAllUsersSelected(newSelected.length === users.length);
    }
  };

  const gerarRelatorio = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/relatorios/gerar`, { 
        ano, 
        mes,
        farmacia_ids: allFarmaciasSelected ? [] : selectedFarmacias,
        user_ids: allUsersSelected ? [] : selectedUsers
      });
      setReport(response.data);
      if (response.data.total_encomendas === 0) {
        toast.info("Sem encomendas para este período/filtros");
      }
    } catch (error) {
      toast.error("Erro ao gerar relatório");
    } finally {
      setLoading(false);
    }
  };

  const enviarRelatorio = async () => {
    setSending(true);
    try {
      const response = await axios.post(`${API}/relatorios/enviar`, { 
        ano, 
        mes,
        farmacia_ids: allFarmaciasSelected ? [] : selectedFarmacias,
        user_ids: allUsersSelected ? [] : selectedUsers
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar relatório");
    } finally {
      setSending(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (!allFarmaciasSelected && selectedFarmacias.length > 0) {
        params.append('farmacia_ids', selectedFarmacias.join(','));
      }
      if (isAdmin && !allUsersSelected && selectedUsers.length > 0) {
        params.append('user_ids', selectedUsers.join(','));
      }
      
      const url = `${API}/relatorios/excel/${ano}/${mes}${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url, { responseType: 'blob' });
      
      const mesNome = MESES.find(m => m.value === mes)?.label || mes;
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `relatorio_${mesNome}_${ano}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel gerado com sucesso");
    } catch (error) {
      toast.error("Erro ao gerar Excel - verifique se há encomendas no período");
    }
  };

  const mesNome = MESES.find(m => m.value === mes)?.label || "";

  return (
    <div className="space-y-6" data-testid="relatorios-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-extrabold text-3xl text-slate-900">
          Relatórios Mensais
        </h1>
        <p className="text-slate-500 mt-1">
          Gere e envie relatórios mensais por email
        </p>
      </div>

      {/* Period Selection & Filters */}
      <Card>
        <CardContent className="p-6 space-y-6">
          {/* Período */}
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="space-y-2 flex-1">
              <Label>Ano</Label>
              <Select value={ano.toString()} onValueChange={(v) => setAno(parseInt(v))}>
                <SelectTrigger data-testid="select-ano">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 flex-1">
              <Label>Mês</Label>
              <Select value={mes.toString()} onValueChange={(v) => setMes(parseInt(v))}>
                <SelectTrigger data-testid="select-mes">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MESES.map((m) => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Filtros */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="font-medium text-slate-700">Filtros</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Filtro Farmácias */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  Farmácias
                </Label>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  <div className="flex items-center space-x-2 pb-2 border-b">
                    <Checkbox 
                      id="all-farmacias" 
                      checked={allFarmaciasSelected}
                      onCheckedChange={toggleAllFarmacias}
                    />
                    <label htmlFor="all-farmacias" className="text-sm font-medium cursor-pointer">
                      Selecionar todas
                    </label>
                  </div>
                  {farmacias.map((f) => (
                    <div key={f.id} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`farm-${f.id}`}
                        checked={selectedFarmacias.includes(f.id)}
                        onCheckedChange={() => toggleFarmacia(f.id)}
                      />
                      <label htmlFor={`farm-${f.id}`} className="text-sm cursor-pointer">
                        {f.nome}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Filtro Utilizadores (só admin) */}
              {isAdmin && (
                <div className="space-y-3">
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-violet-600" />
                    Utilizadores
                  </Label>
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                    <div className="flex items-center space-x-2 pb-2 border-b">
                      <Checkbox 
                        id="all-users" 
                        checked={allUsersSelected}
                        onCheckedChange={toggleAllUsers}
                      />
                      <label htmlFor="all-users" className="text-sm font-medium cursor-pointer">
                        Selecionar todos
                      </label>
                    </div>
                    {users.map((u) => (
                      <div key={u.id} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`user-${u.id}`}
                          checked={selectedUsers.includes(u.id)}
                          onCheckedChange={() => toggleUser(u.id)}
                        />
                        <label htmlFor={`user-${u.id}`} className="text-sm cursor-pointer">
                          {u.nome}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Botão Gerar */}
          <div className="flex justify-end">
            <Button
              onClick={gerarRelatorio}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid="gerar-relatorio-btn"
            >
              <BarChart3 className="w-4 h-4" />
              {loading ? "A gerar..." : "Gerar Relatório"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {report && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Total Encomendas</p>
                    <p className="text-3xl font-bold mt-1">{report.total_encomendas}</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">Valor Total</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">€{report.total_valor.toFixed(2)}</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">Farmácias</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{report.total_farmacias}</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">Utilizadores</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{report.por_utilizador?.length || 0}</p>
                  </div>
                  <div className="p-3 bg-violet-50 rounded-lg">
                    <Users className="w-6 h-6 text-violet-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Actions */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900">
                    Relatório de {mesNome} {ano}
                  </h3>
                  <p className="text-slate-500 text-sm">
                    {allFarmaciasSelected ? "Todas as farmácias" : `${selectedFarmacias.length} farmácia(s)`}
                    {isAdmin && (allUsersSelected ? " • Todos os utilizadores" : ` • ${selectedUsers.length} utilizador(es)`)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={downloadExcel}
                    className="gap-2"
                    data-testid="download-excel-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                    Excel
                  </Button>
                  <Button
                    onClick={enviarRelatorio}
                    disabled={sending || report.total_encomendas === 0}
                    className="bg-blue-600 hover:bg-blue-700 gap-2"
                    data-testid="enviar-relatorio-btn"
                  >
                    <Send className="w-4 h-4" />
                    {sending ? "A enviar..." : "Enviar por Email"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tables */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Por Utilizador */}
            {report.por_utilizador && report.por_utilizador.length > 0 && (
              <Card>
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-violet-600" />
                    Por Utilizador
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/80">
                        <TableHead className="font-bold">Utilizador</TableHead>
                        <TableHead className="font-bold text-center">Enc.</TableHead>
                        <TableHead className="font-bold text-right">Valor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.por_utilizador.map((u, i) => (
                        <TableRow key={i} className="table-row-hover">
                          <TableCell className="font-medium">{u.nome}</TableCell>
                          <TableCell className="text-center">{u.total_encomendas}</TableCell>
                          <TableCell className="text-right font-mono font-semibold text-blue-600">
                            €{u.total_valor.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Por Farmácia */}
            {report.por_farmacia && report.por_farmacia.length > 0 && (
              <Card>
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    Por Farmácia
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/80">
                        <TableHead className="font-bold">Farmácia</TableHead>
                        <TableHead className="font-bold text-center">Enc.</TableHead>
                        <TableHead className="font-bold text-right">Valor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.por_farmacia.map((f, i) => (
                        <TableRow key={i} className="table-row-hover">
                          <TableCell className="font-medium">{f.nome}</TableCell>
                          <TableCell className="text-center">{f.total_encomendas}</TableCell>
                          <TableCell className="text-right font-mono font-semibold text-blue-600">
                            €{f.total_valor.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Por Armazenista */}
            {report.por_armazenista && report.por_armazenista.length > 0 && (
              <Card className="lg:col-span-2">
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Truck className="w-5 h-5 text-orange-600" />
                    Por Armazenista
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/80">
                        <TableHead className="font-bold">Armazenista</TableHead>
                        <TableHead className="font-bold text-center">Enc.</TableHead>
                        <TableHead className="font-bold text-right">Valor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.por_armazenista.map((a, i) => (
                        <TableRow key={i} className="table-row-hover">
                          <TableCell className="font-medium">{a.nome}</TableCell>
                          <TableCell className="text-center">{a.total_encomendas}</TableCell>
                          <TableCell className="text-right font-mono font-semibold text-blue-600">
                            €{a.total_valor.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
